import { useState } from 'react'
import * as Yup from 'yup'
import { Form, Formik, Field } from 'formik'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty, omit } from 'lodash'
import moment from 'moment'
import { toast } from 'react-toastify'

import TextBox from 'components/TextBox'
import Select from 'components/Select'
import InputFullTime from 'components/InputFullTime'
import ToggleButton from 'components/ToggleButton'
import Modal from 'components/Modal'
import Datepicker from 'components/Datepicker'

import {
  getSalesEvent,
  getIsSalesEventsLoading,
} from 'store/SalesEvents/selectors'
import { getCurrentVenueUuid } from 'store/Venues/selector'
import {
  createSalesEvent,
  updateSalesEvent,
} from 'store/SalesEvents/actions'
import {
  getSalesEventTypes,
  getIsSalesEventTypeLoading,
} from 'store/SalesEventTypes/selectors'

const SalesEventsForm = () => {
  const [showValidationErrors, setShowValidationErrors] =
    useState(true)

  const history = useHistory()
  const dispatch = useDispatch()
  const { state: urlQueryParamsState } = useLocation()

  const onCloseModal = () =>
    history.push({ state: { salesEventUuid: null } })

  const salesEventUuid = urlQueryParamsState?.salesEventUuid

  const salesEvent = useSelector((state) =>
    getSalesEvent(state, salesEventUuid)
  )
  const salesEventTypes = useSelector(getSalesEventTypes)
  const venueUuid = useSelector(getCurrentVenueUuid)

  const isSalesEventsLoading = useSelector(getIsSalesEventsLoading)
  const isSalesEventTypeLoading = useSelector(
    getIsSalesEventTypeLoading
  )
  const isLoading = isSalesEventsLoading || isSalesEventTypeLoading

  const isNewSalesEvent = salesEventUuid === 'new'

  const modalTitle = isNewSalesEvent
    ? 'Add Sales Event'
    : 'Edit Sales event'

  const eventDate = !isEmpty(salesEvent.startTime)
    ? moment(salesEvent.startTime).format('MM/DD/yyyy')
    : null

  const initialValues = {
    externalRefId: salesEvent.externalRefId ?? null,
    name: salesEvent.name ?? null,
    salesEventTypeId: salesEvent.salesEventTypeId ?? null,
    date: eventDate,
    time: moment(salesEvent.startTime).format('HH:mm') ?? null,
    locked: salesEvent?.locked ?? false,
  }

  const validationSchema = Yup.object().shape({
    date: Yup.string().required('Required'),
    externalRefId: Yup.string().required('Required'),
    name: Yup.string().required('Required'),
    time: Yup.string().required('Required'),
  })

  const disableUpdateButton = (dirty, errors) => {
    const disableButton =
      !dirty || (showValidationErrors && !isEmpty(errors))

    return disableButton
  }

  const onSubmit = (values) => {
    const startTime = new Date(
      `${moment(values.date).format('MM/DD/YYYY')} ${values.time}`
    ).toISOString()

    const payload = {
      salesEvents: [
        omit({ ...salesEvent, ...values, startTime, venueUuid }, [
          'date',
          'time',
        ]),
      ],
    }

    if (isNewSalesEvent) {
      dispatch(createSalesEvent(payload)).then((response) => {
        // if action payload is empty, then stay on the same page
        if (isEmpty(response?.payload)) return

        history.push({ state: { salesEventUuid: null } })
      })
    } else {
      dispatch(updateSalesEvent(payload)).then((response) => {
        // if action payload is empty, then stay on the same page
        if (isEmpty(response?.payload)) return

        history.push({ state: { salesEventUuid: null } })
      })
    }
  }

  return (
    <Modal
      className="-narrow"
      title={modalTitle}
      onClose={onCloseModal}
      isLoading={isLoading}
      onRequestClose={onCloseModal}
    >
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, values, setFieldValue }) => (
            <Form>
              <div className="item-settings">
                <div className="item-settings__section -with-border-bottom">
                  <h3 className="header-title">Info</h3>
                  <div className="text-field">
                    <Field
                      component={TextBox}
                      label="Internal Name"
                      name="externalRefId"
                    />
                  </div>
                  <Field
                    component={TextBox}
                    label="Display Name"
                    name="name"
                  />
                  <div className="text-field">
                    <Field
                      component={Select}
                      handleChange={(value) => {
                        setFieldValue('salesEventTypeId', value)
                      }}
                      label="Event Type"
                      name="salesEventTypeId"
                      options={salesEventTypes}
                      defaultValue={values?.salesEventTypeId}
                    />
                  </div>
                </div>
                <div className="item-settings__section">
                  <div className="item-settings__section -with-border-bottom">
                    <h3 className="header-title">
                      Start Date & Time
                    </h3>
                    <span className="modal-paragraph -more-space">
                      Time in 24-hour format
                    </span>
                    <div className="text-field">
                      <Datepicker
                        onChange={(value) =>
                          setFieldValue('date', value)
                        }
                        selected={
                          (values.date && new Date(values.date)) ||
                          values.date
                        }
                        name="date"
                        label="Start Date"
                        dateFormat="MM/dd/yyyy"
                      />
                    </div>
                    <div className="text-field">
                      <Field
                        component={InputFullTime}
                        name="time"
                        handleChange={(value) => {
                          setFieldValue('time', value.fullTime)
                        }}
                        time={values?.time ?? ''}
                        isActive={true}
                        isLabel={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="item-settings__section">
                  <h3 className="header-title">Lock event</h3>
                  <span className="modal-paragraph -more-space">
                    When enabled, mobile pre-orders for this event
                    will be unavailable in mobile ordering
                  </span>
                </div>
                <div className="content-wrapper">
                  <span className="content-header">Locked</span>
                  <span className="content-value">
                    <Field
                      component={ToggleButton}
                      name="locked"
                      type="checkbox"
                    />
                  </span>
                </div>
                <div className="btn-wrapper">
                  <button
                    className="btn -update"
                    onClick={() => setShowValidationErrors(true)}
                    disabled={disableUpdateButton(dirty, errors)}
                    type="submit"
                  >
                    Save
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default SalesEventsForm
